import type { ProgramTemplate } from "./templateSchema.js";
import { slot, repeatWeek } from "./templateAuthoring.js";

// One hand-authored program template. Split out of templateData.ts
// (SP-TEMPLATES, 2026-07-27) so every file in this cluster clears the
// 500-line cap on its own, without invoking the data-file exemption.

// Stronglifts 5×5: two alternating workouts (A: Squat / Bench / Row;
// B: Squat / OHP / Deadlift) over 5 weeks at 3 sessions/week. The
// programme convention is "add weight every session" rather than per
// week; here the prescription is the same every week and the athlete
// drives load through the per-set MAX update. Squat lives in both
// sessions on purpose — it's the SL signature.
export const stronglifts5x5: ProgramTemplate = {
  id: "tpl-prog-stronglifts-5x5",
  name: {
    en: "Stronglifts 5×5",
    it: "Stronglifts 5×5",
    fr: "Stronglifts 5×5",
    de: "Stronglifts 5×5",
    ru: "Stronglifts 5×5",
  },
  description: {
    en: "Three sessions per week, two alternating workouts (A and B). Five compound lifts, five sets of five. Add 2.5 kg every session you complete every rep; reset 10 % when you stall twice.",
    it: "Tre sessioni a settimana, due workout alternati (A e B). Cinque fondamentali, cinque serie da cinque. Aggiungi 2,5 kg ogni sessione in cui chiudi tutte le ripetizioni; scarica del 10 % quando stalli due volte.",
    fr: "Trois séances par semaine, deux entraînements alternés (A et B). Cinq exercices polyarticulaires, cinq séries de cinq. Ajoute 2,5 kg à chaque séance où toutes les répétitions passent ; réduis de 10 % après deux blocages.",
    de: "Drei Einheiten pro Woche, zwei abwechselnde Workouts (A und B). Fünf Grundübungen, fünf Sätze à fünf Wiederholungen. Pro Einheit, in der alle Wiederholungen sauber laufen, 2,5 kg drauflegen; nach zwei Stagnationen 10 % deload.",
    ru: "Три тренировки в неделю, два чередующихся воркаута (A и B). Пять базовых упражнений, пять подходов по пять. Добавляй 2,5 кг каждую тренировку, где закрыл все повторы; делай разгрузку на 10 %, когда застрял дважды.",
  },
  difficulty: "beginner",
  tags: ["strength", "linear-progression", "compound", "5x5"],
  cycleWeeks: 5,
  sessionsPerWeek: 3,
  sessions: [
    {
      id: "tpl-prog-sl-session-a",
      name: {
        en: "Workout A — Squat / Bench / Row",
        it: "Workout A — Squat / Bench / Row",
        fr: "Séance A — Squat / Bench / Row",
        de: "Workout A — Squat / Bench / Row",
        ru: "Тренировка A — Squat / Bench / Row",
      },
      slots: [
        slot(
          "tpl-prog-sl-a-squat",
          0,
          "back-squat",
          "Back Squat",
          repeatWeek(5, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
          ]),
          {
            instructions: {
              en: "Add 2.5 kg next session if every set hit five clean reps.",
              it: "Aggiungi 2,5 kg alla prossima sessione se hai chiuso tutte le serie con cinque ripetizioni pulite.",
              fr: "Ajoute 2,5 kg à la séance suivante si toutes les séries passent à cinq répétitions propres.",
              de: "Beim nächsten Training 2,5 kg drauflegen, wenn alle Sätze sauber mit fünf Wiederholungen abgeschlossen wurden.",
              ru: "Прибавь 2,5 кг на следующей тренировке, если все подходы прошли чисто на пять повторов.",
            },
          },
        ),
        slot(
          "tpl-prog-sl-a-bench",
          1,
          "bench-press",
          "Bench Press",
          repeatWeek(5, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
          ]),
        ),
        slot(
          "tpl-prog-sl-a-row",
          2,
          "barbell-row",
          "Barbell Row",
          repeatWeek(5, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
          ]),
        ),
      ],
    },
    {
      id: "tpl-prog-sl-session-b",
      name: {
        en: "Workout B — Squat / OHP / Deadlift",
        it: "Workout B — Squat / OHP / Deadlift",
        fr: "Séance B — Squat / OHP / Deadlift",
        de: "Workout B — Squat / OHP / Deadlift",
        ru: "Тренировка B — Squat / OHP / Deadlift",
      },
      slots: [
        slot(
          "tpl-prog-sl-b-squat",
          0,
          "back-squat",
          "Back Squat",
          repeatWeek(5, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
          ]),
        ),
        slot(
          "tpl-prog-sl-b-ohp",
          1,
          "overhead-press",
          "Overhead Press",
          repeatWeek(5, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
            { reps: 5, intensity: { kind: "percent-of-max", pct: 75 } },
          ]),
        ),
        slot(
          "tpl-prog-sl-b-dead",
          2,
          "deadlift",
          "Deadlift",
          repeatWeek(5, [
            { reps: 5, intensity: { kind: "percent-of-max", pct: 80 } },
          ]),
          {
            instructions: {
              en: "One working set of five. Reset and reset hard.",
              it: "Una sola serie di lavoro da cinque. Resetta ogni ripetizione, e resettala bene.",
              fr: "Une seule série de travail de cinq. Reset à chaque rep, et bien franc.",
              de: "Ein Arbeitssatz à fünf. Bei jeder Wiederholung absetzen, und zwar sauber.",
              ru: "Один рабочий подход на пять. Сбрасывай каждый повтор — жёстко и до конца.",
            },
          },
        ),
      ],
    },
  ],
};
